import React, { useState } from 'react';
import {
    Modal,
    Box,
    Typography,
    TextField,
    Button,
    IconButton,
    Stack,
    CircularProgress
} from '@mui/material';
import { CloseIcon } from 'icons/materialIcons';
import apiClient from '../../../config/axiosConfig';

const RequestIntegrationModal = ({ open, onClose, onSubmitSuccess, onSubmitError }) => {
    const [message, setMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const buttonStyles = {
        padding: '8px 45px',
        textTransform: 'none',
        borderRadius: '26px',
    };

    const isSubmitDisabled = message.trim().length === 0 || isSubmitting;

    const handleClose = () => {
        if (!isSubmitting) {
            setMessage('');
            onClose();
        }
    };

    const handleSubmit = async () => {
        if (!message.trim()) {
            return;
        }

        setIsSubmitting(true);
        try {
            // Get current user info
            const authUser = localStorage.getItem('authUser');
            const user = authUser ? JSON.parse(authUser) : null;
            const teamId = localStorage.getItem('currentTeamId');

            // Use the feature request endpoint (can be updated to a dedicated integration request endpoint later)
            const response = await apiClient.post('/api/email/feature-request', {
                featureRequest: `[Integration Request] ${message.trim()}`
            });

            if (response.data.success) {
                setMessage('');
                if (onSubmitSuccess) {
                    onSubmitSuccess('Integration request submitted successfully!');
                }
                handleClose();
            }
        } catch (error) {
            console.error('Error submitting integration request:', error);
            if (onSubmitError) {
                onSubmitError(error.response?.data?.message || 'Failed to submit integration request');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="request-integration-modal"
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backdropFilter: 'blur(8px)',
                bgcolor: 'rgba(255, 255, 255, 0.8)',
            }}
        >
            <Box
                sx={{
                    position: 'relative',
                    width: { xs: '90%', sm: '75%', md: '70%', lg: '60%' },
                    maxWidth: 700,
                    minHeight: 400,
                    bgcolor: '#ffffff',
                    borderRadius: 5,
                    boxShadow: 24,
                    p: "50px"
                }}
            >
                <IconButton
                    onClick={handleClose}
                    sx={{ position: 'absolute', top: 16, right: 16 }}
                    disabled={isSubmitting}
                >
                    <CloseIcon />
                </IconButton>

                <Typography
                    id="request-integration-modal-title"
                    variant="h5"
                    component="h2"
                    gutterBottom
                >
                    Request an Integration
                </Typography>

                <Typography
                    variant="body1"
                    sx={{ mb: 2, fontSize: '0.9rem', color: '#828282' }}
                >
                    Do you have a request for an integration that would make Touch better for you? Let us know!
                </Typography>

                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Message..."
                    multiline
                    rows={4}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    disabled={isSubmitting}
                    sx={{
                        mb: 2,
                        bgcolor: '#f7f7f7',
                        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                    }}
                />

                <Stack direction="row" spacing={2} justifyContent="flex-end">
                    <Button
                        variant="outlined"
                        onClick={handleClose}
                        disabled={isSubmitting}
                        sx={{
                            ...buttonStyles,
                            bgcolor: 'transparent',
                            color: '#212121',
                            fontWeight: '500',
                            border: '1px solid #c4c4c4',
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={handleSubmit}
                        disabled={isSubmitDisabled}
                        sx={{
                            ...buttonStyles,
                            bgcolor: isSubmitDisabled ? '#e0e0e0' : '#000000',
                            color: isSubmitDisabled ? '#a6a6a6' : '#ffffff',
                        }}
                    >
                        {isSubmitting ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            'Submit'
                        )}
                    </Button>
                </Stack>
            </Box>
        </Modal>
    );
};

export default RequestIntegrationModal;

